/**
 * Scroll Utility Functions
 * Helper functions for scroll-related operations
 */

/**
 * Smooth scroll to element
 * @param {Element} element - Target element
 * @param {number} offset - Offset from top (default: 0)
 */
export function scrollToElement(element, offset = 0) {
  if (!element) {
    console.warn('scrollToElement: Element is null or undefined');
    return;
  }

  const targetPosition = element.offsetTop - offset;

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth'
  });
}

/**
 * Get current scroll position
 * @returns {number} - Current scroll Y position
 */
export function getScrollPosition() {
  return window.scrollY || window.pageYOffset;
}

/**
 * Throttle scroll events using requestAnimationFrame
 * @param {Function} callback - Callback function to execute
 * @returns {Function} - Throttled event handler
 */
export function throttleScroll(callback) {
  let ticking = false;

  return function() {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        callback();
        ticking = false;
      });
      ticking = true;
    }
  };
}

/**
 * Check if element is in viewport
 * @param {Element} element - Element to check
 * @param {number} threshold - Percentage of element that should be visible (0-1)
 * @returns {boolean} - True if element is in viewport
 */
export function isInViewport(element, threshold = 0) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
  const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);

  return vertInView && horInView;
}

/**
 * Debounce function for scroll events
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait = 300) {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
